import { useMemo, useEffect } from "react"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useBackgroundRefresh } from "@hydrologiq/shared/monitoring/hooks/useBackgroundRefresh"
import {
  GetSystemMonitoringGraphsDocument,
  GetSystemMonitoringGraphsQuery,
  GetSystemMonitoringGraphsQueryVariables,
  type GraphRequest,
} from "@hydrologiq/gql-experience/graphql"
import { getSeriesColor } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import type {
  TimeSeriesDataPoint,
  TimeSeriesSeries,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import type { AssetConfig } from "./useTimescaleTimeseries"
import { resolveAssetConfigForSeries } from "../utils/resolveAssetConfigForSeries"
import {
  type GraphCategory,
  deriveGraphCategory,
  getAggregatedColor,
  getGenerationAssetColor,
  FUEL_COLORS,
  GENERATION_COLORS,
  PEAK_COLOR,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/graphColors"

export type { GraphRequest }

export interface PhaseDataPoint {
  timestamp: string
  /** Consumer asset ID (or assetId_connection for connection-based) */
  consumerId: string
  /** Asset display name (the parent's for a Hybrid); absent from older API versions */
  assetName?: string | null
  phase1: number | null
  phase2: number | null
  phase3: number | null
  phase1Peak: number | null
  phase2Peak: number | null
  phase3Peak: number | null
}

type MonitoringRow = GetSystemMonitoringGraphsQuery["getSystemMonitoringGraphs"][number]
type AssetSeries = MonitoringRow["assetBreakdown"][number]
type AggPoint = NonNullable<MonitoringRow["aggregatedData"]>[number]

/** API may return overlay graph rows nested on each primary row (schema may extend generated types). */
type MonitoringRowWithOverlays = MonitoringRow & { overlays?: MonitoringRow[] }

export interface OverlayData {
  graphId: string
  field: string
  unit: string | null
  aggregated?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  breakdown: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
}

export interface TimescaleMonitoringGraphPayload {
  /** System-level aggregated view (line + optional peak / dual metric) */
  aggregated?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  /** Per-asset breakdown (stacked / multi-line) */
  breakdown: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
  }
  /** Whether aggregated series exist (toggle target) */
  hasAggregated: boolean
  /** Overlay data from other graphs (e.g., battery SoC on power generation) */
  overlays?: OverlayData[]
  /** Per-consumer per-phase power data for the Phase Balance Chart */
  phaseBalance?: PhaseDataPoint[]
}

function breakdownToChart(
  seriesList: AssetSeries[],
  assetConfigs?: AssetConfig[],
  category?: GraphCategory
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const timestampSet = new Set<string>()
  seriesList.forEach((assetSeries) => {
    assetSeries.data.forEach((point: { time: string }) => {
      timestampSet.add(point.time)
    })
  })
  const sortedTimestamps = Array.from(timestampSet).sort()

  const dataPoints: TimeSeriesDataPoint[] = sortedTimestamps.map((timestamp) => {
    const point: TimeSeriesDataPoint = { timestamp }
    seriesList.forEach((assetSeries) => {
      const dataPoint = assetSeries.data.find((d: { time: string }) => d.time === timestamp)
      const seriesKey = assetSeries.connection
        ? `${assetSeries.assetId}_${assetSeries.connection}`
        : assetSeries.assetId
      const value = dataPoint?.value ?? null
      point[seriesKey] = value
      
      const peakKey = `${seriesKey}_peak`
      const peakValue =
        dataPoint && "peakValue" in dataPoint
          ? (dataPoint as { peakValue?: number | null }).peakValue
          : undefined
      point[peakKey] = peakValue !== null && peakValue !== undefined ? peakValue : value
    })
    return point
  })

  // Count how many series resolve to the same base label. When a role-based
  // label (e.g. "Site Load") is shared by several contributing assets — for
  // instance a VE.Bus inverter/charger output AND an AC-coupled PV inverter,
  // both tagged as load — using the label as-is renders visually identical,
  // indistinguishable lines. In that case we disambiguate by appending the
  // asset name so each contributor is legible.
  const baseNameFor = (assetSeries: AssetSeries): string =>
    assetSeries.label?.trim() || assetSeries.assetName || assetSeries.assetId
  const baseLabelCounts = new Map<string, number>()
  seriesList.forEach((assetSeries) => {
    const base = baseNameFor(assetSeries)
    baseLabelCounts.set(base, (baseLabelCounts.get(base) ?? 0) + 1)
  })

  const series: TimeSeriesSeries<string[]>[] = seriesList.map((assetSeries, index: number) => {
    const seriesKey = assetSeries.connection
      ? `${assetSeries.assetId}_${assetSeries.connection}`
      : assetSeries.assetId
    const config = resolveAssetConfigForSeries(assetConfigs, assetSeries.assetId, assetSeries.connection)
    const baseName = baseNameFor(assetSeries)
    // Use the Timescale label as-is when it uniquely identifies the series.
    // When multiple series share the same base label, append the asset name
    // (falling back to the asset ID) so contributors are distinguishable.
    const isAmbiguous = (baseLabelCounts.get(baseName) ?? 0) > 1
    const disambiguator = assetSeries.assetName || assetSeries.assetId
    const label =
      isAmbiguous && disambiguator && disambiguator !== baseName
        ? `${baseName} — ${disambiguator}`
        : baseName
    // For generation graphs, resolve colour by asset class or name (solar → yellow, generator → purple)
    const generationColor = category === "generation"
      ? getGenerationAssetColor((assetSeries as any).assetClass, baseName)
      : undefined
    return {
      name: seriesKey,
      color: config?.color || generationColor || getSeriesColor(index, seriesList.length, category),
      label: config?.label || label,
      group: config?.group,
      assetId: assetSeries.assetId,
      assetName: assetSeries.assetName ?? undefined,
      strokeWidth: 2,
      connectNulls: false,
    }
  })

  return { data: dataPoints, series }
}

function aggregatedRowsToChart(
  rows: AggPoint[],
  category?: GraphCategory
): {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
} {
  const hasPeak = rows.some((p) => p.peakValue != null && p.peakValue !== undefined)
  const data: TimeSeriesDataPoint[] = rows.map((point) => ({
    timestamp: point.time,
    value: point.value ?? null,
    // combinedPeak: name "peak" is treated as per-asset peak in shared TimeSeries (hidden by default)
    ...(hasPeak ? { combinedPeak: point.peakValue ?? null } : {}),
  }))
  const aggregatedColor = category ? getAggregatedColor(category) : "#39aed2"
  const series: TimeSeriesSeries<string[]>[] = [
    {
      name: "value",
      color: aggregatedColor,
      label: "Total",
      strokeWidth: 3,
      connectNulls: false,
    },
  ]
  if (hasPeak) {
    series.push({
      name: "combinedPeak",
      color: PEAK_COLOR,
      label: "Combined peak",
      strokeWidth: 2,
      strokeDasharray: "5 5",
      connectNulls: false,
    })
  }
  return { data, series }
}

function mergeSecondaryAggregated(
  primary: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] },
  secondaryRows: AggPoint[],
  secondaryLabel: string,
  secondaryColor: string = GENERATION_COLORS.secondary
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const byTime = new Map(secondaryRows.map((p) => [p.time, p.value ?? null]))
  const data = primary.data.map((row) => {
    const ts = row.timestamp as string
    return {
      ...row,
      secondary: byTime.has(ts) ? byTime.get(ts)! : null,
    }
  })
  const series: TimeSeriesSeries<string[]>[] = [
    ...primary.series,
    {
      name: "secondary",
      color: secondaryColor,
      label: secondaryLabel,
      strokeWidth: 2,
      connectNulls: false,
    },
  ]
  return { data, series }
}

function rowToPayload(
  row: MonitoringRow,
  assetConfigs: AssetConfig[] | undefined,
  category?: GraphCategory
): TimescaleMonitoringGraphPayload {
  const breakdown = breakdownToChart(row.assetBreakdown, assetConfigs, category)

  const hasAgg = Boolean(row.aggregatedData && row.aggregatedData.length > 0)
  let aggregated: TimescaleMonitoringGraphPayload["aggregated"]
  if (hasAgg && row.aggregatedData) {
    let chart = aggregatedRowsToChart(row.aggregatedData, category)
    if (row.secondaryAggregated?.length) {
      chart = mergeSecondaryAggregated(
        chart,
        row.secondaryAggregated,
        "Fuel consumed (L)",
        category === "fuel" ? FUEL_COLORS.secondary : undefined
      )
    }
    aggregated = chart
  }

  const overlays: OverlayData[] | undefined = (row as MonitoringRowWithOverlays).overlays?.map((overlay) => {
    // Derive overlay category from its graphId (e.g., "soc__class_battery" -> battery, "power__aggregationRole_load" -> load)
    let overlayCategory: GraphCategory = "default"
    if (overlay.graphId.includes("__class_battery")) {
      overlayCategory = "battery"
    } else if (overlay.graphId.includes("__role_generation")) {
      overlayCategory = "generation"
    } else if (overlay.graphId.includes("__role_consumer") || overlay.graphId.includes("__aggregationRole_load")) {
      overlayCategory = "load"
    } else if (overlay.graphId.includes("__class_fuel")) {
      overlayCategory = "fuel"
    }
    
    const overlayBreakdown = breakdownToChart(overlay.assetBreakdown, assetConfigs, overlayCategory)
    const hasOverlayAgg = Boolean(overlay.aggregatedData && overlay.aggregatedData.length > 0)
    let overlayAggregated: OverlayData["aggregated"]
    if (hasOverlayAgg && overlay.aggregatedData) {
      // Overlays are single reference lines — strip peakValue so
      // aggregatedRowsToChart never adds a second "combinedPeak" series,
      // which would produce duplicate legend/tooltip entries.
      const overlayRowsNoPeak = overlay.aggregatedData.map((p) => ({ ...p, peakValue: null }))
      overlayAggregated = aggregatedRowsToChart(overlayRowsNoPeak, overlayCategory)
    }

    const entry: OverlayData = {
      graphId: overlay.graphId,
      field: overlay.field,
      unit: overlay.unit ?? null,
      breakdown: overlayBreakdown,
    }
    if (overlayAggregated) {
      entry.aggregated = overlayAggregated
    }
    return entry
  })

  return {
    breakdown,
    aggregated,
    hasAggregated: hasAgg,
    overlays,
    ...(row.phaseBreakdown?.length
      ? {
          phaseBalance: row.phaseBreakdown.map((point) => ({
            timestamp: point.time,
            consumerId: point.connection
              ? `${point.assetId}_${point.connection}`
              : point.assetId,
            // `assetName` is newer than the published GraphQL types, so read it defensively.
            assetName: (point as { assetName?: string | null }).assetName ?? null,
            phase1: point.phase1 ?? null,
            phase2: point.phase2 ?? null,
            phase3: point.phase3 ?? null,
            phase1Peak: point.phase1Peak ?? null,
            phase2Peak: point.phase2Peak ?? null,
            phase3Peak: point.phase3Peak ?? null,
          })),
        }
      : {}),
  }
}

export interface UseSystemMonitoringGraphsParams {
  systemId: string
  start: string
  stop: string
  granularity?: string
  /** Increment to force a refetch for unchanged variables; never sent to GraphQL. */
  refreshKey?: number
  graphs: GraphRequest[]
}

export interface UseSystemMonitoringGraphsResult {
  graphsData: Map<string, TimescaleMonitoringGraphPayload>
  loading: boolean
  error?: Error
  refetch: () => void
}

/**
 * Single batch fetch for all Timescale monitoring graphs on the page.
 */
export const useSystemMonitoringGraphs = (
  params: UseSystemMonitoringGraphsParams,
  assetConfigs?: AssetConfig[]
): UseSystemMonitoringGraphsResult => {
  const { systemId, start, stop, granularity = "5m", refreshKey, graphs } = params

  const { beginFetch, isBackground } = useBackgroundRefresh()

  const [executeQuery, { data, previousData, loading, error }] = useLazyQueryWithEndpoint<
    GetSystemMonitoringGraphsQuery,
    GetSystemMonitoringGraphsQueryVariables
  >(
    GetSystemMonitoringGraphsDocument,
    {
      notifyOnNetworkStatusChange: true,
    },
    "primary"
  )

  useEffect(() => {
    if (graphs.length === 0) {
      return
    }
    beginFetch(refreshKey)
    executeQuery({
      variables: {
        systemId,
        start,
        stop,
        granularity,
        graphs,
      },
    })
  }, [systemId, start, stop, granularity, refreshKey, graphs, executeQuery, beginFetch])

  // An automatic refresh keeps the previous graphs on screen and reports no loading until the new data arrives.
  const background = isBackground()
  const current = data ?? (background && !error ? previousData : undefined)

  const graphsData = useMemo(() => {
    const map = new Map<string, TimescaleMonitoringGraphPayload>()
    const rows = current?.getSystemMonitoringGraphs
    if (!rows?.length) {
      return map
    }
    
    // Create a map from graphId to category
    const categoryMap = new Map<string, GraphCategory>()
    for (const graphReq of graphs) {
      const category = deriveGraphCategory({
        role: graphReq.role ?? undefined,
        class: graphReq.assetClass ?? undefined,
        field: graphReq.field,
      })
      categoryMap.set(graphReq.graphId, category)
    }
    
    for (const row of rows) {
      const category = categoryMap.get(row.graphId)
      const payload = rowToPayload(row, assetConfigs, category)
      map.set(row.graphId, payload)
    }
    return map
  }, [current, assetConfigs, graphs])

  const refetch = () => {
    if (graphs.length === 0) {
      return
    }
    executeQuery({
      variables: {
        systemId,
        start,
        stop,
        granularity,
        graphs,
      },
    })
  }

  return {
    graphsData,
    loading: graphs.length === 0 ? false : loading && !background,
    error: error as Error | undefined,
    refetch,
  }
}
